import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Custom Hook zur Verwaltung aller Statistics-Daten und -Berechnungen
 * Trennt die Datenlogik von der UI-Logik
 */
export const useStatisticsData = () => {
  const statistics = useSelector((state) => state.dartGame.statistics);
  const isLoading = useSelector((state) => state.dartGame.isLoading);
  const dartCount = useSelector((state) => state.dartGame.dartCount);
  const minDartRounds = useSelector((state) => state.dartGame.minDartRounds);
  const roundHistory = useSelector((state) => state.dartGame.roundHistory);
  const isProMember = useSelector((state) => state.user.proMember);
  const user = useSelector((state) => state.user);

  // Berechnete Werte für Basic Statistics
  const basicStatistics = useMemo(() => {
    const totalDartsThrown = roundHistory.reduce(
      (acc, round) => acc + round.hits.length,
      0
    );
    const totalScore = roundHistory.reduce(
      (acc, round) => acc + round.totalScore,
      0
    );
    const threeDartAverage =
      totalDartsThrown > 0
        ? ((totalScore / totalDartsThrown) * 3).toFixed(2)
        : "0.00";
    const highestRound =
      roundHistory.length > 0
        ? Math.max(...roundHistory.map((r) => r.totalScore))
        : 0;
    const averagePerDart =
      totalDartsThrown > 0
        ? (totalScore / totalDartsThrown).toFixed(2)
        : "0.00";

    return {
      totalDartsThrown,
      totalScore,
      threeDartAverage,
      highestRound,
      averagePerDart,
      roundCount: roundHistory.length,
    };
  }, [roundHistory]);

  // Berechnete Werte für Pro Statistics
  const proStatistics = useMemo(() => {
    if (!statistics) return null;

    const optimizedCheckoutCount = statistics?.checkout_stats
      ? Object.values(statistics.checkout_stats).reduce((total, dartsObj) => {
          return (
            total +
            Object.values(dartsObj).filter((path) => path?.changed === true)
              .length
          );
        }, 0)
      : 0;

    return {
      scoringAvgT20: statistics["scoring_avg_T20"],
      scoringAvgT19: statistics["scoring_avg_T19"],
      avgLower: statistics["avg_lower"],
      avgMedian: statistics["avg_median"],
      avgUpper: statistics["avg_upper"],
      dqLower: statistics["dq_lower"],
      dqMedian: statistics["dq_median"],
      dqUpper: statistics["dq_upper"],
      xStdDeviation: statistics["x_std_deviation"],
      yStdDeviation: statistics["y_std_deviation"],
      phi: statistics["phi"],
      generalScore: statistics["general_score"],
      d20OrD16: statistics["D20_or_D16"],
      d20OrD16Likelihood: statistics["D20_or_D16_likelihood"],
      t20OrT19: statistics["T20_or_T19"],
      optimizedCheckoutCount,
      checkoutStats: statistics.checkout_stats,
    };
  }, [statistics]);

  // Status-Berechnungen
  const status = useMemo(() => {
    const totalDartsNeeded = minDartRounds * 3;
    const isDisabled = dartCount < totalDartsNeeded;
    const evaluateIsDisabled = isDisabled || statistics !== null;

    return {
      isDisabled,
      evaluateIsDisabled,
      totalDartsNeeded,
      hasStatistics: !!statistics,
      isProMember,
    };
  }, [dartCount, minDartRounds, statistics, isProMember]);

  return {
    statistics,
    basicStatistics,
    proStatistics,
    status,
    isLoading,
    user,
  };
};
